import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';

import {
  IAutoQuote,
  IPolicyVehicle,
  IVehicle,
  TExcludedVehicleList,
  TOwnershipStatus,
} from '../../interfaces';
import { getFromSessionStorage } from '../../services/sessionStorage';

import { getVehicleName } from './getVehicleName';

export * from './castLienholderValueBasedOnOwnershipStatus';
export * from './getVehicleName';

export function getQuoteVehiclesProps(
  quote: IAutoQuote,
  primaryVehicleDataSource?: string
) {
  const vehiclesList = quote?.application?.vehicles || [];
  const ineligibleVINTableVehiclesList: TExcludedVehicleList[] = (
    quote?.application?.metadata?.removed_vehicles_to_display || []
  ).map((vehicle) => ({
    id: getVehicleId(vehicle),
    title: getVehicleName(vehicle, ['year', 'make', 'model']),
    vin: vehicle.vin,
  }));

  const excludedVehiclesList: TExcludedVehicleList[] = JSON.parse(
    getFromSessionStorage('excludedVehiclesList') || '[]'
  );
  const primaryVehicle = find(vehiclesList, {
    data_source: primaryVehicleDataSource,
  });
  const nonPrimaryVehiclesList = vehiclesList.filter(
    (vehicle: IVehicle) => vehicle.data_source !== primaryVehicleDataSource
  );
  const suggestedVehicles = get(
    quote,
    'application.metadata.suggested_vehicles',
    []
  );
  const remainingSuggestedVehicles = suggestedVehicles.filter(
    (suggestion: IVehicle) => {
      return !find(vehiclesList, { id: suggestion.id });
    }
  );

  const _excludedVehiclesList = ineligibleVINTableVehiclesList.length
    ? excludedVehiclesList.filter((excludedVehicle) =>
        ineligibleVINTableVehiclesList?.find(
          (vehicle) => vehicle.id !== excludedVehicle.id
        )
      )
    : excludedVehiclesList;

  const excludedVehiclesToDisplay = [
    ...ineligibleVINTableVehiclesList,
    ..._excludedVehiclesList,
  ];

  function getVehicleVinBlacklist(editingVehicleVin: string) {
    return filter(
      [...vehiclesList, ...excludedVehiclesToDisplay].map(
        (vehicle: IVehicle | TExcludedVehicleList) => vehicle?.vin
      ),
      (vin: string) => vin !== editingVehicleVin
    );
  }

  function getIsLienholderInfoDisabled(
    editingVehicle: IVehicle,
    disabledLienholderInfoByOwnershipStatus?: {
      lienholder: { name: string };
      ownershipStatus: TOwnershipStatus;
    }[]
  ) {
    if (
      !!editingVehicle &&
      !!primaryVehicle &&
      (<IVehicle>primaryVehicle).id === editingVehicle.id
    ) {
      const disabledLienholderInfo = find(
        disabledLienholderInfoByOwnershipStatus,
        {
          ownershipStatus: editingVehicle.ownership_status,
        }
      );
      return (
        get(editingVehicle, 'lienholder.name') ===
        get(disabledLienholderInfo, 'lienholder.name')
      );
    }
    return false;
  }

  function getVehiclesList() {
    if (primaryVehicle) {
      return [
        primaryVehicle,
        ...vehiclesList.filter((vehicle) => {
          if (vehicle.data_source !== primaryVehicleDataSource) {
            return vehicle;
          }
        }),
      ];
    }

    return vehiclesList;
  }

  return {
    vehiclesList: getVehiclesList(),
    primaryVehicle,
    nonPrimaryVehiclesList,
    excludedVehiclesToDisplay,
    remainingSuggestedVehicles,
    getVehicleVinBlacklist,
    getIsLienholderInfoDisabled,
  };
}

export function getVehicleId(vehicle: IVehicle | IPolicyVehicle) {
  return (vehicle.id || vehicle.vin)?.toString()!;
}
